/**
 * ComplianceSignals — Wave 6.2 RESOLUTION-METRICS-KPI
 *
 * Bottom-right panel.  Three sub-panels:
 *   1. Adverse-Action rate by branch (sorted list with threshold highlight)
 *   2. Processed-zero-artifact gap rows (compliance gap signal)
 *   3. Lobby-ticket precondition violations (must always be 0)
 *
 * All thresholds read from SystemSettings (no hardcoding):
 *   metrics.adverseActionRate.alertThreshold        (default 0.10)
 *   metrics.processedZeroArtifact.alertThreshold    (default 1)
 *   metrics.lobbyTicketUnresolvedClosures.alertThreshold (default 1)
 */

import { useNavigate } from 'react-router';
import type {
  CartResolutionArtifactSignals,
  CartResolutionBranchRate,
  CartResolutionInefficiencySignals,
  CartResolutionRange,
  CartResolutionThresholds,
} from '@retail-ops/shared-types';

interface ComplianceSignalsProps {
  range: CartResolutionRange;
  artifactSignals: CartResolutionArtifactSignals;
  inefficiencySignals: CartResolutionInefficiencySignals;
  thresholds: CartResolutionThresholds;
}

export function ComplianceSignals({
  range,
  artifactSignals,
  inefficiencySignals,
  thresholds,
}: ComplianceSignalsProps): JSX.Element {
  const navigate = useNavigate();

  const aaThreshold = thresholds.adverseActionRateAlert;
  const aaThresholdPct = `${Math.round(aaThreshold * 100)}%`;
  const zeroArtifactThreshold = thresholds.processedZeroArtifactAlert;
  const lobbyThreshold = thresholds.lobbyTicketUnresolvedAlert;

  function drillBranch(branchCode: string): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('branch', branchCode);
    params.set('resolution', 'Adverse-Action');
    navigate(`/metrics/carts?${params.toString()}`);
  }

  function drillZeroArtifact(): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('resolution', 'Processed');
    params.set('artifactCount', '0');
    navigate(`/metrics/carts?${params.toString()}`);
  }

  function drillLobbyViolation(): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('lobbyUnresolvedClosure', '1');
    navigate(`/metrics/carts?${params.toString()}`);
  }

  const flaggedBranches = [...inefficiencySignals.adverseActionRatePerBranch]
    .filter((b: CartResolutionBranchRate) => b.total > 0)
    .sort((a: CartResolutionBranchRate, b: CartResolutionBranchRate) => b.rate - a.rate);

  const lobbyViolation = inefficiencySignals.lobbyTicketUnresolvedClosures >= lobbyThreshold;
  const zeroArtifactGap = artifactSignals.processedZeroArtifactRows >= zeroArtifactThreshold;

  return (
    <div className="compliance-signals">
      {/* Critical: lobby precondition violation — should always be 0 */}
      {lobbyViolation && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg, #fbeaea)',
            border: '1px solid var(--pcb-red)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-6)',
            cursor: 'pointer',
          }}
          onClick={drillLobbyViolation}
        >
          <div style={{ fontWeight: 700, color: 'var(--pcb-red)', fontSize: 12 }}>
            Lobby-ticket precondition violation: {inefficiencySignals.lobbyTicketUnresolvedClosures}
          </div>
          <div style={{ fontSize: 11, color: 'var(--err-text, var(--pcb-red))', marginTop: 2 }}>
            A lobby-origin cart was closed without ever attaching a CIF or account.
            This should always be 0. Click to investigate.
          </div>
        </div>
      )}

      {/* Adverse-action rate by branch */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-900)' }}>
          Adverse-Action rate by branch
        </h4>
        <span style={{ fontSize: 10, color: 'var(--ink-500)' }}>
          (alert at {aaThresholdPct} · admin-tunable)
        </span>
      </header>

      {flaggedBranches.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-5)',
            fontSize: 12,
            color: 'var(--ink-600)',
            background: 'var(--ink-50)',
            borderRadius: 'var(--r-md)',
            textAlign: 'center',
          }}
        >
          No branch data in window.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {flaggedBranches.map((b: CartResolutionBranchRate) => {
            const flagged = b.rate > aaThreshold;
            const tone = flagged ? 'var(--pcb-red)' : 'var(--ink-700)';
            return (
              <li
                key={b.branchCode}
                onClick={() => drillBranch(b.branchCode)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-4)',
                  padding: 'var(--sp-3) 0',
                  borderBottom: '1px solid var(--ink-100)',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                <span style={{ flex: 1, color: 'var(--ink-900)', fontWeight: 600 }}>
                  {b.branchName}{' '}
                  <span style={{ color: 'var(--ink-500)', fontWeight: 400, fontSize: 10 }}>
                    · {b.branchCode}
                  </span>
                </span>
                <span style={{ color: 'var(--ink-500)', fontSize: 11 }}>n={b.total}</span>
                {/* heat bar */}
                <span
                  aria-hidden
                  style={{
                    width: 80,
                    height: 6,
                    background: 'var(--ink-200)',
                    borderRadius: 'var(--r-full)',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: `${Math.min(100, b.rate * 200)}%`,
                      height: '100%',
                      background: tone,
                    }}
                  />
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    color: tone,
                    minWidth: 50,
                    textAlign: 'right',
                  }}
                >
                  {(b.rate * 100).toFixed(1)}%
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Processed-zero-artifact gap */}
      <div
        style={{
          marginTop: 'var(--sp-7)',
          padding: 'var(--sp-5)',
          background: zeroArtifactGap ? 'var(--err-bg, #fbeaea)' : 'var(--ink-50)',
          border: zeroArtifactGap ? '1px solid var(--pcb-red)' : '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          cursor: zeroArtifactGap ? 'pointer' : 'default',
        }}
        onClick={zeroArtifactGap ? drillZeroArtifact : undefined}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-600)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
          }}
        >
          Processed carts with zero regulated artifacts
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: zeroArtifactGap ? 'var(--pcb-red)' : 'var(--pcb-green)',
            fontFamily: 'var(--font-mono)',
            marginTop: 4,
          }}
        >
          {artifactSignals.processedZeroArtifactRows}
        </div>
        <div style={{ fontSize: 10, color: 'var(--ink-600)', marginTop: 2 }}>
          {zeroArtifactGap
            ? `≥ ${zeroArtifactThreshold} — investigation required. Click to view.`
            : 'No gap detected.'}
        </div>
      </div>

      {/* Artifact ratio summaries */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'var(--sp-4)',
          marginTop: 'var(--sp-5)',
          fontSize: 11,
        }}
      >
        <div>
          <div style={{ color: 'var(--ink-600)' }}>Withdrawn artifact rate</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ink-900)' }}>
            {(artifactSignals.withdrawnArtifactRate * 100).toFixed(1)}%
          </div>
        </div>
        <div>
          <div style={{ color: 'var(--ink-600)' }}>Adverse-Action artifact rate</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ink-900)' }}>
            {(artifactSignals.adverseActionArtifactRate * 100).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
}
